import { Avatar, Box, Card, Flex, Text } from '@chakra-ui/react';

import { ClampedTextV2 } from '~/components/clamped-text-v2';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { format, useFormatDate } from '~/hooks/use-format-date';

import { CAPTION_FONT_SIZE, CARD_BORDER_COLOR } from '../constants';
import type { Testimonial } from '../models/testimonials';
import { formatCountLabel } from '../utils/format-count-label';
import { getInitials } from '../utils/get-initials';
import { getTestimonials } from '../utils/get-testimonials';

import { CardCarousel, CarouselTitle } from './card-carousel';

export type GraduateTestimonialsSubsectionProps = {
  programKey: string;
};

const CARD_WIDTH = 220;

/**
 * Long-form quotes run to ~550 characters, well past the fixed-height card the
 * design mocks with a one-line quote. Without a cap the tallest quote sets the
 * height of every card in the row.
 */
const QUOTE_LINE_CLAMP = 6;

/**
 * The survey export is missing a collection date for 27 of the 134 graduates, 12
 * of them the headshot graduates that lead each carousel, so a visible date would
 * read as a hole on the most prominent cards. Flip this on once content fills the
 * gaps; `testimonial.date` already carries every date we do have.
 */
const SHOW_TESTIMONIAL_DATE = false;

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const formattedDate = useFormatDate({ date: testimonial.date, format: format.monthDayYearShort, forceUTC: true });

  return (
    <Card
      as='article'
      border='1px solid'
      borderColor={CARD_BORDER_COLOR}
      borderRadius='8px'
      boxShadow='none'
      flex={`0 0 ${CARD_WIDTH}px`}
      flexDirection='column'
      gap={4}
      overflow='hidden'
      padding={4}
      width={`${CARD_WIDTH}px`}
    >
      <Flex alignItems='center' gap='10px' width='100%'>
        <Avatar
          bg='supportGray.10'
          getInitials={getInitials}
          loading='lazy'
          name={testimonial.name}
          src={testimonial.headshot || undefined}
          sx={{
            '> .chakra-avatar__initials': {
              color: 'black',
            },
          }}
        />
        <Flex flexDirection='column'>
          <Text color='supportGray.100' fontSize='sm' fontWeight='600' lineHeight='sm'>
            {testimonial.name}
          </Text>
          <Text color='supportGray.60' fontSize={CAPTION_FONT_SIZE} lineHeight='sm'>
            {testimonial.profession || 'Graduate'}
          </Text>
        </Flex>
      </Flex>
      <Box as='blockquote'>
        <ClampedTextV2
          color='supportGray.90'
          fontSize='sm'
          lineHeight='md'
          noOfLines={QUOTE_LINE_CLAMP}
          renderButton={({ isClamped, onToggle }) => (
            <Text
              as='button'
              color='primary.blue'
              fontSize='sm'
              fontWeight='700'
              lineHeight='sm'
              textDecoration='underline'
              width='fit-content'
              onClick={onToggle}
            >
              {isClamped ? 'Read More' : 'Minimize'}
            </Text>
          )}
        >
          <Text
            as='span'
            sx={{
              '&::before': { content: '"\\201C"' },
              '&::after': { content: '"\\201D"' },
            }}
          >
            {testimonial.quote}
          </Text>
        </ClampedTextV2>
      </Box>
      {SHOW_TESTIMONIAL_DATE && testimonial.date ? (
        <Text color='supportGray.60' fontSize={CAPTION_FONT_SIZE} lineHeight='sm' marginTop='auto'>
          {formattedDate}
        </Text>
      ) : null}
    </Card>
  );
}

export function GraduateTestimonialsSubsection({ programKey }: GraduateTestimonialsSubsectionProps) {
  const testimonials = getTestimonials(programKey);

  if (!testimonials || testimonials.length === 0) return null;

  function handleArrowClick() {
    trackEvent({
      name: 'Product Page CTA Clicked',
      type: 'click',
      label: 'testimonials_carousel_scrolled',
      additionalAttributes: { product_ids: [programKey] },
    });
  }

  return (
    <ContentContainer>
      <CardCarousel
        label='graduate testimonials'
        title={<CarouselTitle>Udacity graduates</CarouselTitle>}
        meta={
          <Text color='supportGray.100' fontSize='md' lineHeight='md'>
            ({formatCountLabel(testimonials.length, 'Testimonial')})
          </Text>
        }
        slides={testimonials.map((testimonial) => ({
          key: `${testimonial.name}-${testimonial.quote.slice(0, 12)}`,
          content: <TestimonialCard testimonial={testimonial} />,
        }))}
        onArrowClick={handleArrowClick}
      />
    </ContentContainer>
  );
}
